import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import ReuploadApiResponseDialog from '../../../components/ReuploadDocuments/ReuploadApiResponseDialog';
import ReuploadDocumentsPageLayout from '../../../components/ReuploadDocuments/ReuploadDocumentsPageLayout';
import {
    ReuploadDocumentSlot,
    ReuploadDocumentsGrid,
    buildReuploadDocumentRemarks,
    buildReuploadStatusBarFromSlots,
    isDocFlagApproved,
    isDocFlagRejected,
    isReuploadSubmitDisabled,
    preserveReuploadDocumentValue,
    REUPLOAD_DOCUMENT_URL_KEYS,
} from '../../../components/ReuploadDocuments/reuploadDocumentUi';
import { cfpBaseUrl } from '../../../network/urls';
import { handleFileUpload } from '../../../services/Functions';
import { portalName } from '../../../utils/PortalName';

const ReconnectionUpdateDocuments = () => {
    const initialValues = {
        iv_applicant_cnic_document_front: null,
        iv_applicant_cnic_document_back: null,
    };

    const [apiResponseDialog, setApiResponseDialog] = useState({
        open: false,
        type: 'success',
        message: '',
        documentRemarks: [],
    });
    const [submitCompleted, setSubmitCompleted] = useState(false);
    const [applicantCnicDocumentFrontThumbnail, setApplicantCnicDocumentFrontThumbnail] = useState('');
    const [applicantCnicDocumentBackThumbnail, setApplicantCnicDocumentBackThumbnail] = useState('');
    const [applicantCnicFrontError, setApplicantCnicFrontError] = useState('');
    const [applicantCnicBackError, setApplicantCnicBackError] = useState('');
    const [viewBackdrop, setViewBackdrop] = useState(false);
    const [error, setError] = useState(false);
    const [cnicFrontReUploaded, setCnicFrontReUploaded] = useState(false);
    const [cnicBackReUploaded, setCnicBackReUploaded] = useState(false);

    const { state } = useLocation();
    const { dialogRowData } = state || {};
    const { token } = useSelector((authState) => authState.auth);
    const navigate = useNavigate();
    const reuploadSources = [dialogRowData];

    useEffect(() => {
        if (dialogRowData?.applicantCNICFront) {
            setApplicantCnicDocumentFrontThumbnail(dialogRowData.applicantCNICFront);
        }
        if (dialogRowData?.applicantCNICBack) {
            setApplicantCnicDocumentBackThumbnail(dialogRowData.applicantCNICBack);
        }
    }, [dialogRowData]);

    const isCnicFrontRejected = isDocFlagRejected(dialogRowData?.applicantCNICFrontFlag);
    const isCnicBackRejected = isDocFlagRejected(dialogRowData?.applicantCNICBackFlag);
    const isCnicFrontApproved = isDocFlagApproved(dialogRowData?.applicantCNICFrontFlag);
    const isCnicBackApproved = isDocFlagApproved(dialogRowData?.applicantCNICBackFlag);

    const reuploadSlots = useMemo(() => ([
        { isRejected: isCnicFrontRejected, isReUploaded: cnicFrontReUploaded },
        { isRejected: isCnicBackRejected, isReUploaded: cnicBackReUploaded },
    ]), [isCnicFrontRejected, isCnicBackRejected, cnicFrontReUploaded, cnicBackReUploaded]);

    const reuploadStatusBar = useMemo(
        () => buildReuploadStatusBarFromSlots(reuploadSlots),
        [reuploadSlots],
    );

    const disableSubmit = isReuploadSubmitDisabled(reuploadSlots, submitCompleted);

    const { handleChange, handleSubmit } = useFormik({
        initialValues,
        validationSchema: Yup.object({
            iv_applicant_cnic_document_front: Yup.mixed().required(
                'Please upload CNIC front in jpg, jpeg, png or pdf format.',
            ),
            iv_applicant_cnic_document_back: Yup.mixed().required(
                'Please upload CNIC back in jpg, jpeg, png or pdf format.',
            ),
        }),
        onSubmit: async () => {
            setViewBackdrop(true);
            const myHeaders = new Headers();
            myHeaders.append('Content-Type', 'application/json');
            myHeaders.append('Authorization', token);

            const raw = JSON.stringify({
                applicationId: dialogRowData.applicationId,
                submittedThrough: portalName,
                applicationProgressId: 1,
                objectionStatus: 0,
                applicantCNICBack: preserveReuploadDocumentValue(applicantCnicDocumentBackThumbnail, dialogRowData?.applicantCNICBack),
                applicantCNICFront: preserveReuploadDocumentValue(applicantCnicDocumentFrontThumbnail, dialogRowData?.applicantCNICFront),
                paidBillImage: '',
                applicantCNICFrontFlag: 2,
                applicantCNICBackFlag: 2,
                paidBillImageFlag: 2,
                remarksDetails: 'Documents Re-uploaded.',
                remarks: 'Documents Re-uploaded.',
            });

            try {
                const response = await fetch(`${cfpBaseUrl}rco/reUploadRCODocuments`, {
                    method: 'POST',
                    headers: myHeaders,
                    body: raw,
                });
                const result = await response.json();

                if (result?.status === 200 || result?.status === '200') {
                    setSubmitCompleted(true);
                    setApiResponseDialog({
                        open: true,
                        type: 'success',
                        message: result.message || 'Documents re-uploaded successfully.',
                        documentRemarks: buildReuploadDocumentRemarks({}, dialogRowData),
                    });
                } else {
                    setApiResponseDialog({
                        open: true,
                        type: 'error',
                        message: result.message || 'Something went wrong.',
                        documentRemarks: [],
                    });
                }
            } catch (submitError) {
                setApiResponseDialog({
                    open: true,
                    type: 'error',
                    message: 'Failed to re-upload documents.',
                    documentRemarks: [],
                });
            } finally {
                setViewBackdrop(false);
            }
        },
    });

    const handleCnicFrontUpload = (event) => {
        const newFileName = `copyOfApplicantCNICFront.${event.target.files[0].name.split('.').pop()}`;
        handleFileUpload(
            event,
            handleChange,
            () => {},
            setApplicantCnicFrontError,
            setApplicantCnicDocumentFrontThumbnail,
            newFileName,
        );
        setCnicFrontReUploaded(true);
    };

    const handleCnicBackUpload = (event) => {
        const newFileName = `copyOfApplicantCNICBack.${event.target.files[0].name.split('.').pop()}`;
        handleFileUpload(
            event,
            handleChange,
            () => {},
            setApplicantCnicBackError,
            setApplicantCnicDocumentBackThumbnail,
            newFileName,
        );
        setCnicBackReUploaded(true);
    };

    const handleApiResponseDialogClose = () => {
        const wasSuccess = apiResponseDialog.type === 'success';
        setApiResponseDialog((prev) => ({ ...prev, open: false }));
        if (wasSuccess) {
            navigate('/reconnection-table', { state: { refreshReconnectionList: true } });
        }
    };

    return (
        <>
            <ReuploadDocumentsPageLayout
                pageTitle="Reconnection - Re-Upload Documents"
                statusBar={reuploadStatusBar}
                dialogRowData={dialogRowData}
                onSubmit={handleSubmit}
                onCancel={() => navigate('/reconnection-table')}
                submitDisabled={disableSubmit}
                viewBackdrop={viewBackdrop}
                error={error}
                onErrorClose={() => setError('')}
            >
                <ReuploadDocumentsGrid>
                    <ReuploadDocumentSlot
                                    label="CNIC Front"
                                    thumbnail={applicantCnicDocumentFrontThumbnail}
                                    documentSources={reuploadSources}
                                    documentUrlKeys={REUPLOAD_DOCUMENT_URL_KEYS.cnicFront}
                                    isApproved={isCnicFrontApproved}
                                    isRejected={isCnicFrontRejected}
                                    isReUploaded={cnicFrontReUploaded}
                                    inputId="iv_applicant_cnic_document_front"
                                    inputName="iv_applicant_cnic_document_front"
                                    onUpload={handleCnicFrontUpload}
                                    uploadError={applicantCnicFrontError}
                                />
                                <ReuploadDocumentSlot
                                    label="CNIC Back"
                                    thumbnail={applicantCnicDocumentBackThumbnail}
                                    documentSources={reuploadSources}
                                    documentUrlKeys={REUPLOAD_DOCUMENT_URL_KEYS.cnicBack}
                                    isApproved={isCnicBackApproved}
                                    isRejected={isCnicBackRejected}
                                    isReUploaded={cnicBackReUploaded}
                                    inputId="iv_applicant_cnic_document_back"
                                    inputName="iv_applicant_cnic_document_back"
                                    onUpload={handleCnicBackUpload}
                                    uploadError={applicantCnicBackError}
                                />
                </ReuploadDocumentsGrid>
            </ReuploadDocumentsPageLayout>

            <ReuploadApiResponseDialog
                open={apiResponseDialog.open}
                type={apiResponseDialog.type}
                onClose={handleApiResponseDialogClose}
                message={apiResponseDialog.message}
                applicationId={dialogRowData?.applicationId}
                documentRemarks={apiResponseDialog.documentRemarks}
                errorTitle="Request Failed"
            />
        </>
    );
};

export default ReconnectionUpdateDocuments;
